import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { getAnonId } from "@/lib/events";
import { Button } from "@/components/ui/button";

const STEPS = [
  { key: "occasion", q: "Who are you shopping for?", options: [
    ["myself", "Myself"], ["partner", "My partner"], ["gift", "A gift"], ["bridal", "A wedding"]]},
  { key: "metal", q: "Which metal feels like you?", options: [
    ["gold", "Yellow gold"], ["rose", "Rose gold"], ["white", "White gold"], ["silver", "Silver"]]},
  { key: "budget", q: "A comfortable budget?", options: [
    ["under-25k", "Under ₹25k"], ["25-50k", "₹25–50k"], ["50-100k", "₹50k–1L"], ["100k-plus", "₹1L+"]]},
];

export default function StyleQuiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const answer = async (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    setBusy(true);
    try {
      await api.post("/quiz/style", { answers: next, anon_id: getAnonId() });
      setDone(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const metalLabel = STEPS[1].options.find(([v]) => v === answers.metal)?.[1] || "";
  const shopMetal = answers.metal === "silver" ? "silver" : "gold";

  return (
    <section className="container-x mt-16" data-testid="style-quiz">
      <div className="bg-brand rounded-2xl px-6 py-12 sm:px-12 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gold/60" />
        {!done ? (
          <div className="max-w-2xl">
            <p className="eyebrow !text-gold">THE STYLE QUIZ</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-ivory mt-2">Find pieces that feel like you</h2>
            <p className="text-ivory/60 text-sm mt-2">Three taps. We remember your taste — metal, budget and all — for next time.</p>
            <div className="mt-8">
              <p className="text-ivory font-medium" data-testid="quiz-question">{STEPS[step].q}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {STEPS[step].options.map(([value, label]) => (
                  <button key={value} disabled={busy} onClick={() => answer(STEPS[step].key, value)}
                    className="px-5 py-2.5 rounded-full border border-ivory/30 text-ivory text-sm hover:bg-gold hover:text-brand hover:border-gold transition-colors btn-press"
                    data-testid={`quiz-option-${value}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-ivory/40 text-xs font-mono mt-6" data-testid="quiz-progress">{step + 1} / {STEPS.length}</p>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl" data-testid="quiz-result">
            <p className="eyebrow !text-gold">YOUR EDIT IS READY</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-ivory mt-2">{metalLabel} it is.</h2>
            <p className="text-ivory/60 text-sm mt-2">Your taste profile is saved — we'll show you more of what you love as new pieces land.</p>
            <Button onClick={() => navigate(`/shop?metal=${shopMetal}`)}
              className="mt-6 bg-gold text-brand hover:bg-gold-light rounded-full px-7 btn-press font-semibold" data-testid="quiz-shop-cta">
              Shop your edit <ArrowRight size={15} className="ml-1" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
